import React, { useEffect, useMemo, useState } from 'react'
import { cn } from '@utils/safeTs'
import {
  AlignBottom,
  AlignCenter,
  AlignLeft,
  AlignMiddle,
  AlignRight,
  AlignTop,
  Altezza,
  Angle,
  Bin,
  Larghezza,
  RotateLeft,
  RotateRight,
} from '@components/icons'
import styles from './EditorFloatingLayout.module.sass'
import { FormField, FormInput } from '@components/atoms'
import useTrans from '@hooks/useTrans'
import { useEditorContext } from 'context/EditorContext'

interface Props {
  className?: string
}

const EditorFloatingLayout = (props: Props) => {
  const { className = '' } = props

  const t = useTrans()
  const { activeCanvas } = useEditorContext()

  const object = useMemo(() => {
    return activeCanvas?.selectedObject
  }, [activeCanvas])
  const [draftValues, setDraftValues] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!object?.id) {
      setDraftValues({})
      return
    }

    setDraftValues({
      width: object.width != null ? String(object.width) : '',
      height: object.height != null ? String(object.height) : '',
      angle: object.angle != null ? String(object.angle) : '',
    })
  }, [object?.id, object?.width, object?.height, object?.angle])

  const onObjectAction = (action: string, payload?: any) => {
    activeCanvas?.actions?.handleObjectAction(object.id, action, payload)
  }

  const renderInput = (name, icon, label, um) => {
    const isDeferredInput = ['width', 'height', 'angle'].includes(name)
    const value = isDeferredInput ? (draftValues[name] ?? '') : object?.[name] ?? ''

    return (
      <div className={styles.inputWrap}>
        <span className={styles.inputIcon}>{icon}</span>
        <FormField className={styles.field}>
          <FormInput
            id={`editorfloatinglayout_${name}`}
            ariaLabel={label}
            name={name}
            type="number"
            value={value}
            onChange={(e) => {
              const nextValue = e.target.value

              if (isDeferredInput) {
                setDraftValues((prev) => ({ ...prev, [name]: nextValue }))
                return
              }

              if (onObjectAction) {
                onObjectAction('property', { name, value: nextValue })
              }
            }}
            onBlur={(e) => {
              if (!isDeferredInput || !onObjectAction) return
              onObjectAction('property', { name, value: e.target.value })
            }}
            onKeyDown={(e) => {
              if (!isDeferredInput || e.key !== 'Enter') return
              e.currentTarget.blur()
            }}
            className={styles.input}
          />
          <span className={styles.um}>{um}</span>
        </FormField>
      </div>
    )
  }

  const renderButton = (label, icon, onClick) => (
    <button type="button" aria-label={label} onClick={onClick} className={styles.iconButton}>
      {icon}
    </button>
  )

  return object ? (
    <div className={cn(styles.root, className)}>
      <div className={cn(styles.block, styles['block--size'])}>
        {renderInput('width', <Larghezza />, t('Larghezza'), t('cm'))}
        {renderInput('height', <Altezza />, t('Altezza'), t('cm'))}
      </div>
      <div className={cn(styles.block, styles['block--rotate'])}>
        {renderInput('angle', <Angle />, t('Angolo'), t('°'))}
        {renderButton(t('Ruota di 90 gradi in senso orario'), <RotateRight />, () =>
          onObjectAction
            ? onObjectAction('property', {
                name: 'angle',
                value: (parseInt(object.angle) || 0) + 45,
              })
            : {}
        )}
        {renderButton(t('Ruota di 90 gradi in senso antiorario'), <RotateLeft />, () =>
          onObjectAction
            ? onObjectAction('property', {
                name: 'angle',
                value: (parseInt(object.angle) || 0) - 45,
              })
            : {}
        )}
      </div>
      <div className={cn(styles.block, styles['block--alignment'])}>
        {renderButton(t('Allinea a sinistra'), <AlignLeft />, () =>
          onObjectAction ? onObjectAction('align', { value: 'left' }) : {}
        )}
        {renderButton(t('Allinea al centro'), <AlignCenter />, () =>
          onObjectAction ? onObjectAction('align', { value: 'centerHorizontal' }) : {}
        )}
        {renderButton(t('Allinea a destra'), <AlignRight />, () =>
          onObjectAction ? onObjectAction('align', { value: 'right' }) : {}
        )}
        {renderButton(t('Allinea in alto'), <AlignTop />, () =>
          onObjectAction ? onObjectAction('align', { value: 'top' }) : {}
        )}
        {renderButton(t('Allinea verticalmente al centro'), <AlignMiddle />, () =>
          onObjectAction ? onObjectAction('align', { value: 'centerVertical' }) : {}
        )}
        {renderButton(t('Allinea in basso'), <AlignBottom />, () =>
          onObjectAction ? onObjectAction('align', { value: 'bottom' }) : {}
        )}
      </div>
      <div className={cn(styles.block, styles['block--delete'])}>
        {renderButton(t('Rimuovi'), <Bin />, () =>
          onObjectAction ? onObjectAction('delete') : {}
        )}
      </div>
    </div>
  ) : null
}

export default EditorFloatingLayout
